import { z } from 'zod';

/**
 * A login denial the app has a dedicated screen for. Values are the machine
 * codes the Post-Login Action prefixes onto `error_description`.
 */
export enum LoginDenialReason {
	EmailUnverified = 'email_unverified',
	DocumentCompliance = 'document_compliance',
	WorkEmailRequired = 'work_email_required',
}

/**
 * Screen each denial reason lands on. Exhaustive over the enum, so a new reason
 * cannot ship without a destination.
 */
const LOGIN_DENIAL_DESTINATIONS = {
	[LoginDenialReason.EmailUnverified]: '/verify-email',
	[LoginDenialReason.DocumentCompliance]: '/documents-pending',
	[LoginDenialReason.WorkEmailRequired]: '/work-email-required',
} as const satisfies Record<LoginDenialReason, string>;

/**
 * Resolves the screen a denied login should land on.
 *
 * @param reason - A recognized denial reason.
 * @returns The route of the screen dedicated to that reason.
 */
export function getLoginDenialDestination(
	reason: LoginDenialReason,
): (typeof LOGIN_DENIAL_DESTINATIONS)[LoginDenialReason] {
	return LOGIN_DENIAL_DESTINATIONS[reason];
}

/**
 * Shape of an Auth0 `access_denied` rejection. Thrown by the SPA SDK, so it is
 * parsed rather than cast — anything else (a network failure, a non-Error) fails
 * the parse and is treated as an unrecognized denial.
 */
const auth0DenialSchema = z.object({
	error: z.literal('access_denied'),
	error_description: z.string(),
});

/**
 * Unprefixed compliance deny message. Matched verbatim so this screen works
 * whichever order the frontend and the Action are deployed in; remove it once
 * the Action is known to send `document_compliance|…` everywhere.
 */
const DOCUMENT_COMPLIANCE_DENY_MESSAGE =
	'Your organization requires administrator acceptance of updated legal documents.';

/**
 * Classifies a login rejection into the denial reason it represents.
 *
 * @param error - The thrown value from `handleRedirectCallback`.
 * @returns The matching reason, or null when the rejection is not a recognized
 * denial and so must propagate as a genuine fault.
 */
export function getLoginDenialReason(error: unknown): LoginDenialReason | null {
	const parsed = auth0DenialSchema.safeParse(error);
	if (!parsed.success) {
		return null;
	}

	const description = parsed.data.error_description;
	const code = description.split('|')[0];

	if (code === LoginDenialReason.EmailUnverified) {
		return LoginDenialReason.EmailUnverified;
	}

	if (code === LoginDenialReason.DocumentCompliance || description === DOCUMENT_COMPLIANCE_DENY_MESSAGE) {
		return LoginDenialReason.DocumentCompliance;
	}

	if (code === LoginDenialReason.WorkEmailRequired) {
		return LoginDenialReason.WorkEmailRequired;
	}

	return null;
}

/**
 * Pulls the Auth0 subject the Post-Login Action appends after the reason code in a login denial.
 *
 * @param error - The thrown value from `handleRedirectCallback`.
 * @returns The Auth0 subject, or null when the denial carries none or carries prose instead.
 */
export function getLoginDenialSubject(error: unknown): string | null {
	const parsed = auth0DenialSchema.safeParse(error);
	if (!parsed.success) {
		return null;
	}

	const [, ...rest] = parsed.data.error_description.split('|');
	const subject = rest.join('|');

	return subject.includes('|') && !/\s/.test(subject) ? subject : null;
}
